import './App.css';
import { logoutUser } from './api/userApi';
import './axiosInterceptors';
import { OrganizationContext } from './contexts/OrganizationContext';
import { PermissionsContext } from './contexts/PermissionsContext';
import { ProductsContext } from './contexts/ProductsContext';
import { Product } from './interfaces/S1';
import { Organization, User } from './interfaces/User';
import BatchDeterminationPage from './pages/BatchDetermination/BatchDeterminationPage';
import ImportPage from './pages/BatchDetermination/importProcessSlidingPanel/ImportPage';
import GTDCredentialsPage from './pages/settings/gtdCredentials/GTDCredentialsPage';
import ReportSettingsPage from './pages/settings/reports/ReportSettingsPage';
import Loader from './shared/components/Loader';
import { BD_R_ORGANIZATION } from './shared/constants/constants';
import { useMutation } from '@tanstack/react-query';
import { SovosGlobalLayout } from 'mosaic-react';
import { List, Upload } from 'mosaic-react-icons';
import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
	requiredPermission?: string;
	children: JSX.Element;
}

const App: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const logoutMutation = useMutation(logoutUser);
	const { orgTree, selectedOrg, setSelectedAccount } = useContext(OrganizationContext);
	const { areAllPermissionsFetched, isUserPermissionAllowed } = useContext(PermissionsContext);
	const { products, switchProduct } = useContext(ProductsContext);
	const userInfo: User = JSON.parse(sessionStorage.getItem('userInfo') || '');
	const [isAllDataLoaded, setIsAllDataLoaded] = useState(false);

	useEffect(() => {
		setIsAllDataLoaded(areAllPermissionsFetched && orgTree !== null && products !== null);
		if (window.location.pathname === '/') {
			sessionStorage.removeItem('selectedTransaction');
			sessionStorage.removeItem('processId');
		}
	}, [areAllPermissionsFetched, orgTree, products]);

	const navLinks = [
		{
			id: '/',
			label: 'Uploads',
			icon: Upload,
			action: () => navigate('/'),
			isDefault: true,
		},
		{
			id: '/settings',
			label: 'Account Setup',
			icon: List,
			requiredPermission: BD_R_ORGANIZATION,
			nestedLinks: [
				{
					label: 'GTD Credentials',
					action: () => navigate('/gtd-credentials'),
				},
				{
					label: 'Report Settings',
					action: () => navigate('/report-settings'),
				},
			],
		},
	];

	const navLinksIds = navLinks.flatMap(link => {
		return link.id;
	});

	const topLinkPath = `/${location.pathname.split('/')[1]}`;

	const navProps = {
		accounts: orgTree,
		selectedAccount: selectedOrg,
		links: navLinks.filter(link => isUserPermissionAllowed(link.requiredPermission)),
		initialSelectedLink: '',
		selectedLink: navLinksIds.includes(topLinkPath) ? topLinkPath : '/settings',
		slotProps: {
			productSwitcher: {
				selectedProduct: 'Batch Determination Portal',
				products,
				onSelectProduct: (product: Product) => switchProduct(product),
			},
			userMenu: {
				name: userInfo ? userInfo.firstName : '',
				surname: userInfo ? userInfo.lastName : '',
				items: [
					{
						label: 'Logout',
						action: () => {
							logoutMutation.mutate();
						},
					},
				],
			},
			helpButton: {
				onClick: () => {},
			},
		},
		setAccount: (account: Organization) => setSelectedAccount(account),
	};

	const ProtectedRoute = ({ requiredPermission = BD_R_ORGANIZATION, children }: ProtectedRouteProps): JSX.Element =>
		isUserPermissionAllowed(requiredPermission) ? (
			<SovosGlobalLayout NavigationProps={navProps} width='100%'>
				{children}
			</SovosGlobalLayout>
		) : (
			<Navigate to='/' replace />
		);

	return (
		<>
			{!isAllDataLoaded && (
				<div className={`isAllDataLoaded alloy-loading`}>
					<Loader />
				</div>
			)}
			<Routes>
				<Route
					path='/'
					element={
						<ProtectedRoute requiredPermission=''>
							<BatchDeterminationPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/report-settings'
					element={
						<ProtectedRoute>
							<ReportSettingsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path='/gtd-credentials'
					element={
						<ProtectedRoute>
							<GTDCredentialsPage />
						</ProtectedRoute>
					}
				/>
				<Route path='/import' element={<ImportPage />} />
			</Routes>
		</>
	);
};

export default App;
